import React, { useEffect, useMemo, useRef, useState } from "react";
import Notifications from "./Notifications";
import NotificationToast from "./NotificationToast";

const badgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 22,
  height: 22,
  padding: "0 8px",
  borderRadius: 999,
  background: "#ef4444",
  color: "#fff",
  fontSize: 12,
  fontWeight: 700,
};

const actionButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "8px 12px",
  fontSize: 13,
  color: "#4f46e5",
  backgroundColor: "#f8faff",
  borderRadius: 10,
  textDecoration: "none",
  border: "1px solid #e0e7ff",
  transition: "all 0.2s ease-in-out",
  fontWeight: 600,
  cursor: "pointer",
};

export default function NotificationCenter({
  tabs,
  activeTab,
  onTabChange,
  onBulkMarkRead,
  onUndo,
  onDelete,
  loading,
  compact = false,
  title = "Notifications",
  onClose,
}) {
  const [internalActiveTab, setInternalActiveTab] = useState(
    activeTab || (tabs[0] ? tabs[0].key : "")
  );
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [toasts, setToasts] = useState([]);
  const toastTimers = useRef({});

  const resolvedActiveTab = activeTab || internalActiveTab;

  const currentTab = useMemo(
    () => tabs.find((tab) => tab.key === resolvedActiveTab) || tabs[0],
    [resolvedActiveTab, tabs]
  );

  useEffect(() => {
    setToasts([]);
    setSelectionMode(false);
    setSelectedIds([]);
  }, [resolvedActiveTab]);

  useEffect(() => {
    if (activeTab) {
      setInternalActiveTab(activeTab);
    }
  }, [activeTab]);

  const pushToast = (toast) => {
    const id = Date.now() + Math.random();
    const entry = { ...toast, id, exiting: false };
    setToasts((prev) => [...prev, entry]);

    const exitDelay = toast.autoDismiss ?? 30000;
    toastTimers.current[id] = window.setTimeout(() => {
      dismissToastById(id);
    }, exitDelay);
  };

  const dismissToastById = (id) => {
    setToasts((prev) =>
      prev.map((t) => (t.id === id ? { ...t, exiting: true } : t))
    );
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
      clearTimeout(toastTimers.current[id]);
      delete toastTimers.current[id];
    }, 400);
  };

  const unreadIds = useMemo(
    () =>
      (currentTab?.items || [])
        .filter((item) => Number(item.is_read) === 0)
        .map((item) => item.id),
    [currentTab]
  );

  const handleStartBulkRead = () => {
    if (!unreadIds.length) {
      return;
    }

    setSelectedIds(unreadIds);
    setSelectionMode(true);
  };

  const handleToggleSelect = (notificationId) => {
    setSelectedIds((prev) =>
      prev.includes(notificationId)
        ? prev.filter((id) => id !== notificationId)
        : [...prev, notificationId]
    );
  };

  const handleCancelSelection = () => {
    setSelectionMode(false);
    setSelectedIds([]);
  };

  const handleConfirmSelection = async () => {
    if (!selectedIds.length) {
      handleCancelSelection();
      return;
    }

    setSubmitting(true);
    const affectedIds = [...selectedIds];
    const success = await onBulkMarkRead(currentTab.key, affectedIds);
    setSubmitting(false);

    if (!success) {
      return;
    }

    pushToast({
      variant: "success",
      title: "Notifications marked as read",
      message: `${affectedIds.length} notification${affectedIds.length !== 1 ? "s" : ""} marked as read. You can undo or delete them.`,
      tabKey: currentTab.key,
      notificationIds: affectedIds,
      hasActions: true,
    });
    handleCancelSelection();
  };

  const dismissToast = (id) => dismissToastById(id);

  const handleUndo = async (toastEntry) => {
    const success = await onUndo(toastEntry.tabKey, toastEntry.notificationIds);
    if (success) {
      dismissToastById(toastEntry.id);
      pushToast({
        variant: "success",
        title: "Undo successful",
        message: `${toastEntry.notificationIds.length} notification${toastEntry.notificationIds.length !== 1 ? "s" : ""} restored to unread.`,
        autoDismiss: 5000,
      });
    }
  };

  const handleDelete = async (toastEntry) => {
    const success = await onDelete(toastEntry.tabKey, toastEntry.notificationIds);
    if (success) {
      dismissToastById(toastEntry.id);
      pushToast({
        variant: "error",
        title: "Notifications deleted",
        message: `${toastEntry.notificationIds.length} notification${toastEntry.notificationIds.length !== 1 ? "s" : ""} permanently removed.`,
        autoDismiss: 5000,
      });
    }
  };

  const handleTabChange = (tabKey) => {
    setInternalActiveTab(tabKey);
    if (onTabChange) {
      onTabChange(tabKey);
    }
  };

  return (
    <div
      style={{
        position: "relative",
        background: "#fff",
        borderRadius: compact ? 0 : 18,
      }}
    >
      <div
        style={{
          display: "flex",
          gap: compact ? 8 : 12,
          borderBottom: "1px solid #e5e7eb",
          marginBottom: 16,
          overflowX: "auto",
          paddingBottom: 2,
        }}
      >
        {tabs.map((tab) => {
          const unreadCount = tab.items.filter((item) => Number(item.is_read) === 0).length;
          const isActive = tab.key === currentTab.key;

          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => handleTabChange(tab.key)}
              style={{
                border: "none",
                background: "transparent",
                padding: compact ? "10px 8px 12px" : "12px 10px 14px",
                color: isActive ? "#4f46e5" : "#334155",
                fontWeight: isActive ? 700 : 600,
                borderBottom: isActive ? "3px solid #6366f1" : "3px solid transparent",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                cursor: "pointer",
                whiteSpace: "nowrap",
                outline: "none",
              }}
            >
              <span>{tab.label}</span>
              {unreadCount > 0 ? <span style={badgeStyle}>{unreadCount > 99 ? "99+" : unreadCount}</span> : null}
            </button>
          );
        })}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 10,
          marginBottom: selectionMode ? 0 : 16,
        }}
      >
        <div
          style={{
            color: "#64748b",
            fontSize: compact ? 12 : 13,
            fontWeight: 600,
          }}
        >
          Unread notifications are visually highlighted for quick identification.
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={handleStartBulkRead}
            disabled={!unreadIds.length || selectionMode}
            style={{
              ...actionButtonStyle,
              opacity: unreadIds.length && !selectionMode ? 1 : 0.5,
              cursor: unreadIds.length && !selectionMode ? "pointer" : "not-allowed",
            }}
          >
            <em className="icon ni ni-check-circle"></em>
            Mark All as Read
          </button>

          {currentTab?.viewAllHref ? (
            <a href={currentTab.viewAllHref} style={actionButtonStyle}>
              <em className="icon ni ni-eye"></em>
              View All
            </a>
          ) : null}
        </div>
      </div>

      {selectionMode ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
            margin: "10px 0 16px",
            padding: "12px 14px",
            borderRadius: 12,
            background: "#eef2ff",
            border: "1px solid #c7d2fe",
          }}
        >
          <div style={{ color: "#3730a3", fontWeight: 600, fontSize: 13 }}>
            {selectedIds.length} notification{selectedIds.length !== 1 ? "s" : ""} selected. Uncheck any before confirming.
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              onClick={handleConfirmSelection}
              disabled={submitting || !selectedIds.length}
              style={{
                ...actionButtonStyle,
                background: "#4f46e5",
                color: "#fff",
                borderColor: "#4f46e5",
                opacity: submitting || !selectedIds.length ? 0.5 : 1,
                cursor: submitting || !selectedIds.length ? "not-allowed" : "pointer",
              }}
            >
              {submitting ? "Saving..." : "Confirm"}
            </button>
            <button
              type="button"
              onClick={handleCancelSelection}
              disabled={submitting}
              style={{
                ...actionButtonStyle,
                opacity: submitting ? 0.5 : 1,
                cursor: submitting ? "not-allowed" : "pointer",
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      <div
        style={{
          border: "none",
          borderRadius: 0,
          overflow: "visible",
          background: "transparent",
          minHeight: compact ? 280 : 360,
        }}
      >
        {loading ? (
          <>
            <style>{`
              @keyframes ncSkeletonShimmer {
                0%   { background-position: -400px 0; }
                100% { background-position: 400px 0; }
              }
              .nc-skeleton-line {
                border-radius: 6px;
                background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
                background-size: 800px 100%;
                animation: ncSkeletonShimmer 1.4s ease-in-out infinite;
              }
            `}</style>
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 14,
                  padding: "16px 18px",
                  borderRadius: 14,
                  background: "#fff",
                  border: "1px solid #f1f5f9",
                  marginBottom: 10,
                  opacity: 1 - (i - 1) * 0.2,
                }}
              >
                {/* Icon circle */}
                <div
                  className="nc-skeleton-line"
                  style={{ width: 38, height: 38, borderRadius: "50%", flexShrink: 0 }}
                />

                {/* Text lines */}
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8, paddingTop: 2 }}>
                  <div className="nc-skeleton-line" style={{ height: 14, width: "60%" }} />
                  <div className="nc-skeleton-line" style={{ height: 12, width: "90%" }} />
                  <div className="nc-skeleton-line" style={{ height: 10, width: "30%" }} />
                </div>

                {/* Dot indicator */}
                <div
                  className="nc-skeleton-line"
                  style={{ width: 10, height: 10, borderRadius: "50%", flexShrink: 0, marginTop: 6 }}
                />
              </div>
            ))}
          </>
        ) : currentTab?.items?.length ? (
          <div
            style={{
              display: "grid",
              gap: 12,
            }}
          >
            {(compact ? currentTab.items.slice(0, 10) : currentTab.items).map((notification) => (
              <Notifications
                key={`${currentTab.key}-${notification.id}`}
                notification={notification}
                selectable={selectionMode}
                selected={selectedIds.includes(notification.id)}
                onToggleSelect={handleToggleSelect}
                compact={compact}
              />
            ))}
          </div>
        ) : (
          <div
            style={{
              padding: 24,
              color: "#64748b",
              textAlign: "center",
              border: compact ? "none" : "1px dashed #cbd5e1",
              borderRadius: compact ? 0 : 16,
              background: compact ? "transparent" : "#f8fafc",
            }}
          >
            {currentTab?.emptyText || "No notifications yet."}
          </div>
        )}
      </div>

      {toasts.length > 0 ? (
        <div
          style={{
            position: "fixed",
            top: 24,
            right: 24,
            zIndex: 99999,
            display: "flex",
            flexDirection: "column",
            gap: 10,
            minWidth: 340,
            maxWidth: 440,
          }}
        >
          {toasts.map((t) => (
            <NotificationToast
              key={t.id}
              type={t.variant}
              title={t.title}
              message={t.message}
              duration={t.autoDismiss || 30000}
              onDismiss={() => dismissToast(t.id)}
              actions={
                t.hasActions
                  ? [
                      { label: "Undo", icon: "ni-undo", onClick: () => handleUndo(t) },
                      { label: "Delete", icon: "ni-trash", variant: "danger", onClick: () => handleDelete(t) },
                    ]
                  : undefined
              }
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
